import { EntityLabel, NavigationLink } from '@esg/esg-global-types';
import { apollo_client } from './db_util';
import { GET_NAVIGATION_LINK_QUERY } from '../graphql/queries/navigation_link';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { CompanyExtended } from './company';
import { DocumentReference } from 'firebase/firestore';
import {
  CREATE_NAVIGATION_LINK_MUTATOR,
  DELETE_NAVIGATION_LINK_MUTATOR,
  UPDATE_NAVIGATION_LINK_MUTATOR
} from '../graphql/mutations/navigation_link';

export type NavigationLinkData = Omit<NavigationLink, 'id'>;

export interface NavigationLinkExtended extends NavigationLink {
  company: CompanyExtended;
}

// Singular and plural label for model entity.
export const navigation_link_label: EntityLabel = {
  one: 'Navigation Link',
  many: 'Navigation Links'
};

/**
 * Query all NavigationLink documents for all companies or specific companies.
 * @param {string | undefined} group_id Optional specific Group to query Navigation Links for.
 * @param {string | undefined} company_id Optional specific Company to query Navigation Links for.
 * @param {string | undefined} id Optional query for a specific Navigation Link document.
 * @returns {Promise<Array<NavigationLink>>}
 */
export const getNavigationLinks = async (
  group_id?: string,
  company_id?: string,
  id?: string
): Promise<Array<NavigationLink>> => {
  try {
    const navigation_links: Array<NavigationLink> = await apollo_client
      .query({
        query: GET_NAVIGATION_LINK_QUERY,
        variables: { group_id: group_id, company_id: company_id, id: id },
        fetchPolicy: 'no-cache'
      })
      .then((response) => response.data.navigation_links);
    return navigation_links;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: getNavigationLinks failed on an unknown error.',
      {
        group_id: group_id,
        company_id: company_id,
        id: id
      },
      tracking_id
    );
  }
};

/**
 * Create NavigationLink with relative data.
 * @param {string} group_id ID of Group which NavigationLink is being created for.
 * @param {string} company_id ID of Company which NavigationLink is being created for.
 * @param {NavigationLinkData} navigation_link_data NavigationLink data to create document with.
 * @param {string} user_email User creating Navigation Link for audit logs.
 * @returns {Promise<DocumentReference>}
 */
export const createNavigationLink = async (
  group_id: string,
  company_id: string,
  navigation_link_data: NavigationLinkData,
  user_email: string
): Promise<DocumentReference> => {
  try {
    const navigation_link_doc: DocumentReference = await apollo_client
      .mutate({
        mutation: CREATE_NAVIGATION_LINK_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          navigation_link_data: navigation_link_data,
          user_email: user_email
        }
      })
      .then((response) => response.data.createNavigationLink);
    return navigation_link_doc;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/navigation_link.ts failed on an unknown error while calling createNavigationLink.',
      {
        group_id: group_id,
        company_id: company_id,
        navigation_link_data: navigation_link_data,
        user_email: user_email
      },
      tracking_id
    );
  }
};

/**
 * Update Navigation Link with relative data.
 * @param {string} group_id ID of Group to update Navigation Link for.
 * @param {string} company_id ID of Company to update Navigation Link for.
 * @param {string} navigation_link_id ID of Navigation Link to update.
 * @param {NavigationLinkData} updated_navigation_link New Navigation Link data to push into document.
 * @param {NavigationLinkData} original_navigation_link Original Navigation Link data to overwrite.
 * @param {string} user_email User updating Navigation Link for audit logs.
 * @returns {Promise<void>}
 */
export const updateNavigationLink = async (
  group_id: string,
  company_id: string,
  navigation_link_id: string,
  updated_navigation_link: NavigationLinkData,
  original_navigation_link: NavigationLinkData,
  user_email: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: UPDATE_NAVIGATION_LINK_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          navigation_link_id: navigation_link_id,
          updated_navigation_link: updated_navigation_link,
          original_navigation_link: original_navigation_link,
          user_email: user_email
        }
      })
      .then((response) => response.data.updateNavigationLink);
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/navigation_link.ts failed on an unknown error while calling updateNavigationLink.',
      {
        group_id: group_id,
        company_id: company_id,
        navigation_link_id: navigation_link_id,
        updated_navigation_link: updated_navigation_link,
        original_navigation_link: original_navigation_link,
        user_email
      },
      tracking_id
    );
  }
};

/**
 * Delete configured Navigation Link.
 * @param {string} group_id ID of Group to delete Navigation Link for.
 * @param {string} company_id ID of Company to delete Navigation Link for.
 * @param {string} navigation_link_id ID of Navigation Link to delete.
 * @param {string} user_email User deleting Navigation Link for audit logs.
 * @returns {Promise<void>}
 */
export const deleteNavigationLink = async (
  group_id: string,
  company_id: string,
  navigation_link_id: string,
  user_email: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: DELETE_NAVIGATION_LINK_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          navigation_link_id: navigation_link_id,
          user_email: user_email
        }
      })
      .then((response) => response.data.deleteNavigationLink);
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/navigation_link.ts failed on an unknown error while calling deleteNavigationLink.',
      {
        group_id: group_id,
        company_id: company_id,
        navigation_link_id: navigation_link_id,
        user_email: user_email
      },
      tracking_id
    );
  }
};
